import {
  Badge,
  Box,
  Button,
  ComboboxItem,
  Flex,
  Grid,
  Loader,
  LoadingOverlay,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useDebouncedState, useDisclosure } from "@mantine/hooks";
import { IconBarcode, IconFileExport, IconSearch } from "@tabler/icons-react";
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_RowSelectionState,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useDeferredValue, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  repositoryDelivery,
  repositoryPos,
} from "../../../_base/_const/_constVar";
import _breadcrumb from "../../../_base/component/_layout/_breadcrumb";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import { changeSearchValue } from "../../../_base/helper/FunctionHelper";
import MultiSelectCustom from "../../../common/MultiSelectCustom/MultiSelectCustom";
import { BASE_API_DELIVERY } from "../../../config";
import { _localization } from "../../../config/location";
import { green } from "../../../const/variables";
import { MessageResponse } from "../../../model/MessageResponse";

type ColumnPinningType = {
  left?: string[];
  right?: string[];
};

// Footer tổng tiền của các cột giá, lấy từ priceListDetailsSum trả về kèm danh sách
const renderPriceFooter = (total: any) => (
  <Text fw={"bold"} size="18px" c={"red"}>
    {Number(total ?? 0).toLocaleString()} VNĐ
  </Text>
);

// Cell tiền chung cho tất cả các cột giá
const renderPriceCell = ({ renderedCellValue }: any) =>
  renderedCellValue ? (
    <Text size="12.5px" fw={500}>
      {Number(renderedCellValue)?.toLocaleString()} VNĐ
    </Text>
  ) : (
    ""
  );

const CheckSummaryPrice = () => {
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [data, setData] = useState<any[]>([]);
  const [dataSum, setDataSum] = useState<any>({});
  const [dataInfo, setDataInfo] = useState<any>({});
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [height, setHeight] = useState(0);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 50,
  });
  const rowSelectionDeferred =
    useDeferredValue<MRT_RowSelectionState>(rowSelection);
  const [selectIds, setSelectIds] = useState<string[]>([]);
  const [visible, { toggle, open, close }] = useDisclosure(false);

  const location = useLocation();
  const _searchJson = sessionStorage.getItem(String(location.pathname))
    ? JSON.parse(sessionStorage.getItem(String(location.pathname)) ?? "")
    : null;
  const checkedPinSearch = localStorage.getItem("checkedPinSearch") == "true";
  const [search, setSearch] = useState<any>({
    ItemCodes: _searchJson && checkedPinSearch ? _searchJson?.ItemCodes : [],
  });

  // MultiSelectCustom đã tự debounce trước khi gọi onSearchChange
  const [searchItemCode, setSearchItemCode] = useState<string>("");
  const [dataItemCode, setDataItemCode] = useState<ComboboxItem[]>([]);

  // Chỉ được phép gọi API khi đã chọn mã sản phẩm
  const hasItemCodes =
    Array.isArray(search.ItemCodes) && search.ItemCodes.length > 0;

  // Đếm số lần bấm tìm kiếm, = 0 nghĩa là mới vào trang (chưa call API lần nào)
  const [searchTrigger, setSearchTrigger] = useState(0);

  // Chỉ hiển thị các cột có Footer (các cột tổng tiền)
  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "itemCode",
        header: "Mã sản phẩm",
        Cell: ({ renderedCellValue }) => (
          <Badge
            radius="sm"
            variant="dot"
            size="md"
            style={{ cursor: "pointer" }}
            color={renderedCellValue === null ? "red" : green.base}
          >
            {renderedCellValue ?? "Không có mã sản phẩm"}
          </Badge>
        ),
      },
      {
        accessorKey: "demoPrice",
        header: "Giá thị trường",
        Cell: renderPriceCell,
        Footer: () => renderPriceFooter(dataSum?.demoPrice),
      },
      {
        accessorKey: "listPrice",
        header: "Giá bán lẻ",
        Cell: renderPriceCell,
        Footer: () => renderPriceFooter(dataSum?.listPrice),
      },
      {
        accessorKey: "ecommercePrice",
        header: "Giá TMĐT",
        Cell: renderPriceCell,
        Footer: () => renderPriceFooter(dataSum?.ecommercePrice),
      },
      {
        accessorKey: "priceWithoutPromotion",
        header: "Giá không lấy KM",
        Cell: renderPriceCell,
        Footer: () => renderPriceFooter(dataSum?.priceWithoutPromotion),
      },
      {
        accessorKey: "webPrice",
        header: "Giá Build PC kèm VGA",
        Cell: renderPriceCell,
        Footer: () => renderPriceFooter(dataSum?.webPrice),
        size: 225,
      },
      {
        accessorKey: "giaBuildPcKoVga",
        header: "Giá Build PC",
        Cell: renderPriceCell,
        Footer: () => renderPriceFooter(dataSum?.giaBuildPcKoVga),
        size: 225,
      },
      {
        accessorKey: "wholesalePrice1",
        header: "Giá bán buôn 1",
        Cell: renderPriceCell,
        Footer: () => renderPriceFooter(dataSum?.wholesalePrice1),
      },
      {
        accessorKey: "wholesalePrice2",
        header: "Giá bán buôn 2",
        Cell: renderPriceCell,
        Footer: () => renderPriceFooter(dataSum?.wholesalePrice2),
      },
      {
        accessorKey: "wholesalePrice3",
        header: "Giá bán buôn 3",
        Cell: renderPriceCell,
        Footer: () => renderPriceFooter(dataSum?.wholesalePrice3),
      },
      {
        accessorKey: "discountAmount1",
        header: "Tỷ lệ giảm giá 1",
        Cell: renderPriceCell,
        Footer: () => renderPriceFooter(dataSum?.discountAmount1),
      },
      {
        accessorKey: "discountAmount2",
        header: "Tỷ lệ giảm giá 2",
        Cell: renderPriceCell,
        Footer: () => renderPriceFooter(dataSum?.discountAmount2),
      },
      {
        accessorKey: "discountAmount3",
        header: "Tỷ lệ giảm giá 3",
        Cell: renderPriceCell,
        Footer: () => renderPriceFooter(dataSum?.discountAmount3),
      },
    ],
    [dataSum],
  );

  useEffect(() => {
    //   startTransition(() => {
    const valuesList = Object.keys(rowSelectionDeferred);
    setSelectIds(valuesList);
    if (valuesList.length < 1) setSelectIds([]);
    else {
      const valuesList = Object.keys(rowSelectionDeferred);
      setSelectIds(valuesList);
    }
    //  });
  }, [rowSelectionDeferred]);

  function getColorStatus(text: any) {
    switch (text) {
      case 1:
        return "teal";
      case 2:
        return "red";
      default:
        return "yellow";
    }
  }

  const buildParams = () => {
    const params = new URLSearchParams();
    Object.entries(search).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        value.forEach((item) => params.append(key, String(item)));
      } else if (typeof value === "string" && value.length > 0) {
        params.append(key, value);
      }
    });
    return params;
  };

  const fetchData = async () => {
    // Chặn call API khi chưa chọn mã sản phẩm
    if (!hasItemCodes) {
      NotificationExtension.Warn("Vui lòng chọn mã sản phẩm !");
      return;
    }

    setIsLoading(true);
    setIsRefetching(true);
    setIsError(false);
    try {
      let url = `/api/v1/PriceList/get-list?Skip=${
        pagination.pageIndex * pagination.pageSize
      }&Take=${pagination.pageSize}`;

      const params = buildParams();

      url += `&${params.toString()}`;

      const res = await repositoryDelivery.get<MessageResponse<any>>(url);

      if (res && res.success) {
        const result = res.data;
        setData(result?.priceListDetails);
        setDataInfo({
          id: result.id,
          priceListNum: result.priceListNum,
          description: result.description,
          status: result.status,
          isDrap: result.isDrap,
          listIdBranch: result.listIdBranch,
          activeDate: result.activeDate,
          inactiveDate: result.inactiveDate,
          attribute1: result.attribute1,
          attribute2: result.attribute2,
          attribute3: result.attribute3,
          attribute4: result.attribute4,
          attribute5: result.attribute5,
          attribute6: result.attribute6,
          attribute7: result.attribute7,
          attribute8: result.attribute8,
          attribute9: result.attribute9,
          attribute10: result.attribute10,
          attribute11: result.attribute11,
          attribute12: result.attribute12,
          attribute13: result.attribute13,
          attribute14: result.attribute14,
          attribute15: result.attribute15,
          createBy: result.createBy,
          createDate: result.createDate,
          lastUpdateBy: result.lastUpdateBy,
          lastUpdateDate: result.lastUpdateDate,
          approveBy: result.approveBy,
          approveDate: result.approveDate,
        });
        setRowCount(res.totalCount);
        setDataSum(result.priceListDetailsSum);
      } else {
        setData([]);
        setRowCount(0);
        setDataSum({});
      }
    } catch (error) {
      setData([]);
      setIsError(true);
      console.error(error);
      return;
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
      sessionStorage.setItem(
        String(location.pathname),
        JSON.stringify({
          ...search,
        }),
      );
    }
  };

  const handleSearch = () => {
    if (!hasItemCodes) {
      NotificationExtension.Warn("Vui lòng chọn mã sản phẩm !");
      return;
    }
    setSearchTrigger((prev) => prev + 1);
  };

  const handleExportExcel = async () => {
    // Chặn call API khi chưa chọn mã sản phẩm
    if (!hasItemCodes) {
      NotificationExtension.Warn("Vui lòng chọn mã sản phẩm !");
      return;
    }

    open();
    try {
      let url = `/api/v1/PriceList/export-excel?Skip=${
        pagination.pageIndex * pagination.pageSize
      }&Take=${pagination.pageSize}`;

      const params = buildParams();

      url += `&${params.toString()}`;

      const _url = BASE_API_DELIVERY + url;
      const token = localStorage.getItem("token");

      const response = await fetch(_url, {
        method: "GET",
        headers: {
          "Content-Type": "application/vnd.ms-excel",
          Authorization: `Bearer ${token?.replace(`"`, "").replace(`"`, "")}`,
        },
      });

      if (!response.ok) {
        NotificationExtension.Fails("Export Excel thất bại !");
      } else {
        NotificationExtension.Success("Export Excel thành công !");
        const blob = await response.blob();
        const urlss = window.URL.createObjectURL(blob);
        const _name = new Date().toISOString().slice(0, 10);
        let filename = "kiem_tra_tong_gia" + _name + ".xlsx";
        const link = document.createElement("a");
        link.href = urlss;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      close();
    }
  };

  const handleGetDataItemCode = async () => {
    if (searchItemCode.includes(",")) return;

    let url = "/api/v1/TblItem/get-select?Page=50";

    if (searchItemCode.length > 0) {
      url += `&Key=${searchItemCode}`;
    }

    const res = await repositoryPos.get<MessageResponse<any>>(url);

    if (res && res.success) {
      const data = res.data
        .filter((i: any) => i.text && i.value)
        .map((i: any) => ({
          label: String(i.text),
          value: String(i.value),
        }));

      setDataItemCode(data);
    }
  };

  // Lưu ghim cột - Function để lấy cấu hình từ localStorage
  const getColumnPinningStorage = () => {
    try {
      const columnPinningStorage = localStorage.getItem(
        "checkSummaryPriceColumnPinning",
      );
      return columnPinningStorage
        ? JSON.parse(columnPinningStorage)
        : {
            left: ["mrt-row-select", "mrt-row-numbers", "itemCode"],
          }; // Giá trị mặc định
    } catch (error) {
      console.error("Error parsing column pinning storage:", error);
      return {
        left: ["mrt-row-select", "mrt-row-numbers"],
      };
    }
  };

  // State cho columnPinning - khởi tạo từ localStorage
  const [columnPinning, setColumnPinning] = useState<ColumnPinningType>(() =>
    getColumnPinningStorage(),
  );

  // Effect để lưu columnPinning vào localStorage mỗi khi có thay đổi
  useEffect(() => {
    try {
      localStorage.setItem(
        "checkSummaryPriceColumnPinning",
        JSON.stringify(columnPinning),
      );
    } catch (error) {
      console.error("Error saving column pinning to localStorage:", error);
    }
  }, [columnPinning]);

  // Mới vào trang (searchTrigger = 0) không call API,
  // chỉ call khi đã bấm tìm kiếm hoặc đổi phân trang sau đó
  useEffect(() => {
    if (searchTrigger > 0) fetchData();
  }, [pagination, searchTrigger]);

  useEffect(() => {
    handleGetDataItemCode();
  }, [searchItemCode]);

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      setHeight(window.innerHeight - (160 + headerHeight));
    };

    handleResize(); // Set initial height
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const table = useMantineReactTable({
    columns,
    data,
    enableColumnResizing: false,
    positionToolbarAlertBanner: "bottom",
    enableRowNumbers: true,
    //add custom action buttons to top-left of top toolbar
    enableTopToolbar: false,
    enableTableFooter: true,
    mantineTopToolbarProps: {
      style: {
        borderBottom: "3px solid rgba(128, 128, 128, 0.5)",
        marginBottom: 5,
      },
    },
    localization: _localization,
    enableRowSelection: false,
    enableStickyFooter: true,
    getRowId: (row) => row.id?.toString(),
    initialState: {
      showColumnFilters: false,
      columnPinning: {
        ...columnPinning,
      },
      columnVisibility: { id: true, phone: false, fax: false },
      density: "xs",
    },
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height },
    },
    enableStickyHeader: true,
    manualPagination: true,
    onRowSelectionChange: setRowSelection,
    rowCount: rowCount,
    onPaginationChange: setPagination,
    mantineTableBodyCellProps: ({ row }) => ({
      style: {
        fontWeight: "500", //conditional styling
        fontSize: "12.5px",
        padding: "5px 15px",
      },
    }),
    state: {
      isLoading,
      pagination: pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      showSkeletons: isLoading, //loading for the first time with no data
      rowSelection,
      columnPinning,
    },
    onColumnPinningChange: setColumnPinning,
    mantineToolbarAlertBannerProps: isError
      ? { color: "red", children: "Lỗi tải dữ liệu !" }
      : undefined,
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      rowsPerPageOptions: ["50", "100", "200"],
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
    columnFilterDisplayMode: "popover",
    mantineTableBodyRowProps: ({ row }) => ({
      style: { cursor: "pointer" },
    }),
  });

  return (
    <>
      <div
        ref={headerRef}
        style={{
          width: "100%",
        }}
      >
        <LoadingOverlay
          visible={visible}
          zIndex={1000}
          overlayProps={{ radius: "sm", blur: 5 }}
          loaderProps={{
            children: (
              <Flex direction={"column"} align="center" gap="xs">
                <Loader type="dots" size={35}></Loader>
                <Title size={15}> Đang xử lý....</Title>
              </Flex>
            ),
          }}
        />
        <Flex justify={"space-between"} align={"center"} w={"100%"}>
          <_breadcrumb></_breadcrumb>
          {/* <Button
            leftSection={<IconFileExport size={14} />}
            onClick={() => handleExportExcel()}
          >
            Export Excel
          </Button> */}
        </Flex>
        <Grid mt={10} w={"100%"}>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 5 }}>
            <MultiSelectCustom
              placeholder="Mã sản phẩm"
              data={dataItemCode}
              value={search.ItemCodes ?? []}
              onChange={(val) => changeSearchValue(val, "ItemCodes", setSearch)}
              searchValue={searchItemCode}
              onSearchChange={setSearchItemCode}
              allowMultipleInput
              nothingFoundMessage="Không tìm thấy mã sản phẩm"
              leftSection={<IconBarcode color="#15aabf" />}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Button
              onClick={() => handleSearch()}
              leftSection={<IconSearch size={14} />}
            >
              Tìm kiếm
            </Button>
          </Grid.Col>
        </Grid>
      </div>
      <Box mt={10}>
        <MantineReactTable table={table} />
      </Box>
    </>
  );
};

export default CheckSummaryPrice;
